import { type AxiosError, type AxiosRequestConfig } from "axios";
import EnvironmentVariables from "@/EnvironmentVariables";
import { AuthType, authService } from "../../services/auth-service";
import { buildAuthorizationHeaders } from "../../services/dpop/dpop-authorization";
import { UrlHelper } from "../url-helper-service";
import { type IHttpService } from "./http-service";

const v1QuerySting = "api-version=1.0";

/** Pre-auth branding / CX settings calls must not trigger MSAL loginRedirect. */
function isAnonymousBrandingRequest(url: string | undefined): boolean {
  if (!url) {
    return false;
  }
  const lower = url.toLowerCase();
  return (
    lower.includes("cxapplicationsettings") || lower.includes("getmspcxsetting")
  );
}

/** Public doc POST is anonymous; that host's CORS does not allow `credentials: include`. */
function isPublicFunctionDocumentUrl(url: string | undefined): boolean {
  if (!url) {
    return false;
  }
  const base = EnvironmentVariables.publicFunctionUrl().replace(/\/+$/, "");
  if (!base) {
    return false;
  }
  const pathOnly = url.split("?")[0] ?? url;
  return pathOnly === `${base}/api/document`;
}

function applyMagicLinkCredentials(config: AxiosRequestConfig) {
  if (authService.getAuthType() !== AuthType.MagicLink) {
    return;
  }
  if (isPublicFunctionDocumentUrl(config.url)) {
    config.withCredentials = false;
    return;
  }
  config.withCredentials = true;
  config.xsrfCookieName = "XSRF-TOKEN";
  config.xsrfHeaderName = "X-XSRF-TOKEN";
}
export function setupRequestInterceptor(HttpService: IHttpService) {
  HttpService.interceptors.request.use(
    async (config: AxiosRequestConfig) => {
      const UseAzureAdB2C = (window as any).UseAzureAdB2C as string;
      // const idToken = LocalStorageService.getIdToken();
      let accessToken = "";
      if (UrlHelper.isPublicURL() || isAnonymousBrandingRequest(config.url)) {
        accessToken = authService.getLocallySavedAccessToken() || "";
      } else {
        accessToken = (await getAccessToken()) || "";
      }
      if (
        accessToken == "" &&
        UseAzureAdB2C == "True" &&
        !UrlHelper.isPublicURL()
      ) {
        UrlHelper.redirectToSignIn();
        return {};
      }

      if (UseAzureAdB2C == "False") {
        const { technician } = HttpService.store!.state;
        let RoleId = technician!.roleId;
        let ServiceProviderId = technician!.serviceProviderId;
        let TechnicainId = technician!.technicianId;
        const ServiceProviderType = technician!.serviceProviderType;
        const BaseRoleIds = technician!.baseRoleIds;
        const TechnicianGroupClientIds: number[] =
          technician!.technicianGroupClientIds;
        const OtherGroupClientIds: number[] = technician!.otherGroupClientIds;
        const IsSuperAdmin: boolean = technician!.isSignupUser;
        const TechnicianSubscribedGroupIds: number[] =
          technician!.subscribedGroupIds;
        RoleId = RoleId || 0;
        ServiceProviderId = ServiceProviderId || 0;
        TechnicainId = TechnicainId || 0;
        // that means url contains query string need to append version no
        if (config.url != null && config.url.indexOf("?") != -1) {
          config.url =
            config.url +
            "&RoleId=" +
            RoleId +
            "&ServiceProviderId=" +
            ServiceProviderId +
            "&TechnicianId=" +
            TechnicainId +
            "&ServiceProviderType=" +
            ServiceProviderType;
          if (BaseRoleIds && BaseRoleIds.length > 0) {
            config.url += "&BaseRoleIds=" + BaseRoleIds.join();
          }

          if (
            TechnicianGroupClientIds != null &&
            TechnicianGroupClientIds.length > 0
          ) {
            config.url +=
              "&TechnicianGroupClientIds=" + TechnicianGroupClientIds.join();
          }

          if (OtherGroupClientIds != null && OtherGroupClientIds.length > 0) {
            config.url += "&OtherGroupClientIds=" + OtherGroupClientIds.join();
          }

          config.url += "&IsSuperAdmin=" + IsSuperAdmin;

          if (
            TechnicianSubscribedGroupIds != null &&
            TechnicianSubscribedGroupIds.length > 0
          ) {
            config.url +=
              "&TechnicianSubscribedGroupIds=" +
              TechnicianSubscribedGroupIds.join();
          }

          if (config.url.indexOf("api-version") == -1)
            config.url = config.url + "&" + v1QuerySting;
        } else {
          config.url =
            config.url +
            "?" +
            "RoleId=" +
            RoleId +
            "&ServiceProviderId=" +
            ServiceProviderId +
            "&TechnicianId=" +
            TechnicainId +
            "&ServiceProviderType=" +
            ServiceProviderType;
          if (BaseRoleIds && BaseRoleIds.length > 0) {
            config.url += "&BaseRoleIds=" + BaseRoleIds.join();
          }

          if (
            TechnicianGroupClientIds != null &&
            TechnicianGroupClientIds.length > 0
          ) {
            config.url +=
              "&TechnicianGroupClientIds=" + TechnicianGroupClientIds.join();
          }

          if (OtherGroupClientIds != null && OtherGroupClientIds.length > 0) {
            config.url += "&OtherGroupClientIds=" + OtherGroupClientIds.join();
          }

          config.url += "&IsSuperAdmin=" + IsSuperAdmin;

          if (
            TechnicianSubscribedGroupIds != null &&
            TechnicianSubscribedGroupIds.length > 0
          ) {
            config.url +=
              "&TechnicianSubscribedGroupIds=" +
              TechnicianSubscribedGroupIds.join();
          }

          if (config.url.indexOf("api-version") == -1)
            config.url = config.url + "&" + v1QuerySting;
        }

        applyMagicLinkCredentials(config);

        return {
          ...config,
          headers: {
            ...config.headers,
            "Ocp-Apim-Subscription-Key": (window as any)
              .ApiSubscriptionKey as string,
            "api-auth": window.apiAuthCode as string,
            RoleId: RoleId,
            ServiceProviderId: ServiceProviderId,
            AppType: "ClientPortal",
          },
        };
      } else {
        if (config.url != null && config.url.indexOf("api-version") == -1) {
          if (config.url.indexOf("?") != -1) {
            config.url = config.url + "&" + v1QuerySting;
          } else {
            config.url = config.url + "?" + v1QuerySting;
          }
        }

        applyMagicLinkCredentials(config);

        // DPoP for gateway calls when enabled (services/dpop), otherwise the Bearer header as always.
        // The response interceptor retries a 401 by sending this config through here again with
        // isRetry set; the first retry exchanges afresh, any later one goes as Bearer.
        const retryState = config as AxiosRequestConfig & {
          isRetry?: boolean;
          dpopRetried?: boolean;
        };
        const isRetry = retryState.isRetry === true;
        const auth = await buildAuthorizationHeaders(
          config.method,
          () => HttpService.getUri(config),
          accessToken,
          isRetry,
          isRetry && retryState.dpopRetried === true,
        );
        if (isRetry) retryState.dpopRetried = true;

        const headers: Record<string, any> = {
          ...config.headers,
          "Ocp-Apim-Subscription-Key": (window as any)
            .ApiSubscriptionKey as string,
          Authorization: auth.Authorization,
          Accept: "application/json",
          AppType: "ClientPortal",
        };
        // A retry that fell back to Bearer must not carry the previous attempt's proof along.
        delete headers.DPoP;
        delete headers.dpop;
        if (auth.DPoP) headers.DPoP = auth.DPoP;

        return {
          ...config,
          headers,
        };
      }
    },
    /* ignore next */
    (error: AxiosError) => {
      return Promise.reject(error);
    },
  );
}

async function getAccessToken() {
  let accessToken = authService.getLocallySavedAccessToken();
  if (accessToken != "") {
    return accessToken;
  } else {
    return await authService.getAccessToken();
  }
}
